import { useState } from "react";
import { motion } from "framer-motion";
import { Plus, Trash2, Eye, EyeOff, SlidersHorizontal, Pencil, RotateCcw, X } from "lucide-react";
import { CATEGORIES as BASE_CATEGORIES } from "@/content/categories";
import { INTERVIEW_ROLES as BASE_ROLES, INTERVIEW_TYPES as BASE_TYPES, type InterviewQuestion } from "@/content/interview";
import type { TriageLens } from "@/content/triage";
import type { ChallengeTemplate } from "@/core/data/types";
import { useCustomContentStore, useChallenges, useQuestions, useLenses, useConceptAxes, roleName, typeName } from "@/store/customContentStore";
import { ChallengeEditor, QuestionEditor, LensEditor, DocFormatEditor, DocSectionEditor } from "@/features/customize/StudioEditors";
import { useDesignDocContentStore, useDocFormats, useDocSections } from "@/store/designDocContentStore";
import type { DocFormat, DocSection } from "@/content/designDocs";

const GROUPS = ["Design", "Craft", "Production", "Business"];

type Tab = "disciplines" | "concepts" | "filters" | "tags" | "challenges" | "interview" | "triage" | "docs";

const TABS: { id: Tab; label: string; ready: boolean }[] = [
  { id: "disciplines", label: "Disciplines", ready: true },
  { id: "concepts", label: "Game Concepts", ready: true },
  { id: "filters", label: "Interview Filters", ready: true },
  { id: "tags", label: "Tags", ready: true },
  { id: "challenges", label: "Challenges", ready: true },
  { id: "interview", label: "Interview Qs", ready: true },
  { id: "triage", label: "Triage Lenses", ready: true },
  { id: "docs", label: "Design Docs", ready: true },
];

export default function Customize() {
  const [tab, setTab] = useState<Tab>("disciplines");

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="grid h-11 w-11 place-items-center rounded-xl bg-surface-2 text-primary">
          <SlidersHorizontal className="h-6 w-6" />
        </div>
        <div>
          <h2 className="font-display text-2xl font-semibold text-text">Customize</h2>
          <p className="text-sm text-muted">Add or remove your own content. Built-ins are hidden (not deleted) so nothing breaks; your additions are fully removable.</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => t.ready && setTab(t.id)}
            disabled={!t.ready}
            className={[
              "inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors",
              tab === t.id ? "border-primary bg-primary/10 text-text" : "border-border bg-surface text-muted",
              t.ready ? "hover:text-text" : "cursor-not-allowed opacity-50",
            ].join(" ")}
          >
            {t.label}
            {!t.ready && <span className="rounded-full bg-surface-2 px-1.5 text-[10px] uppercase">soon</span>}
          </button>
        ))}
      </div>

      {tab === "disciplines" && <DisciplinesEditor />}
      {tab === "filters" && <FiltersEditor />}
      {tab === "challenges" && <ChallengesEditor />}
      {tab === "interview" && <InterviewQuestionsEditor />}
      {tab === "concepts" && <ConceptsEditor />}
      {tab === "tags" && <TagsEditor />}
      {tab === "triage" && <TriageLensesEditor />}
      {tab === "docs" && <DesignDocsEditor />}
    </div>
  );
}

function DesignDocsEditor() {
  const formats = useDocFormats();
  const sections = useDocSections();
  const isCustomF = useDesignDocContentStore((s) => s.isCustomFormat);
  const isEditedF = useDesignDocContentStore((s) => s.isEditedFormat);
  const removeF = useDesignDocContentStore((s) => s.removeFormat);
  const revertF = useDesignDocContentStore((s) => s.revertFormat);
  const isCustomS = useDesignDocContentStore((s) => s.isCustomSection);
  const isEditedS = useDesignDocContentStore((s) => s.isEditedSection);
  const removeS = useDesignDocContentStore((s) => s.removeSection);
  const revertS = useDesignDocContentStore((s) => s.revertSection);
  const [fEditor, setFEditor] = useState<{ open: boolean; item: DocFormat | null }>({ open: false, item: null });
  const [sEditor, setSEditor] = useState<{ open: boolean; item: DocSection | null }>({ open: false, item: null });

  return (
    <div className="space-y-8">
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted">{formats.length} formats. Edit a built-in to override it, or add your own document type.</p>
          <button onClick={() => setFEditor({ open: true, item: null })} className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-sm font-semibold text-primary-fg shadow-glow transition-transform hover:scale-[1.03]">
            <Plus className="h-4 w-4" /> Add format
          </button>
        </div>
        {formats.map((f) => (
          <ItemRow
            key={f.id}
            title={f.name}
            meta={`${f.blurb} · ${f.sectionIds.length} sections`}
            custom={isCustomF(f.id)}
            edited={isEditedF(f.id)}
            onEdit={() => setFEditor({ open: true, item: f })}
            onRemove={() => removeF(f.id)}
            onRevert={isEditedF(f.id) ? () => revertF(f.id) : undefined}
          />
        ))}
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted">{sections.length} sections in the shared catalog. Formats pick from these.</p>
          <button onClick={() => setSEditor({ open: true, item: null })} className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-surface-2 px-3 py-1.5 text-sm font-medium text-text hover:bg-border/40">
            <Plus className="h-4 w-4" /> Add section
          </button>
        </div>
        {sections.map((s) => (
          <ItemRow
            key={s.id}
            title={s.title}
            meta={s.guidance}
            custom={isCustomS(s.id)}
            edited={isEditedS(s.id)}
            onEdit={() => setSEditor({ open: true, item: s })}
            onRemove={() => removeS(s.id)}
            onRevert={isEditedS(s.id) ? () => revertS(s.id) : undefined}
          />
        ))}
      </div>

      <p className="text-xs text-muted">
        Formats and sections here power the Design Docs section. Hidden built-ins drop out of the format picker and editor; documents you've already written keep their text.
      </p>

      {fEditor.open && <DocFormatEditor format={fEditor.item} onClose={() => setFEditor({ open: false, item: null })} />}
      {sEditor.open && <DocSectionEditor section={sEditor.item} onClose={() => setSEditor({ open: false, item: null })} />}
    </div>
  );
}

function TriageLensesEditor() {
  const lenses = useLenses();
  const isCustom = useCustomContentStore((s) => s.isCustomLens);
  const isEdited = useCustomContentStore((s) => s.isEditedLens);
  const removeLens = useCustomContentStore((s) => s.removeLens);
  const revertLens = useCustomContentStore((s) => s.revertLens);
  const [editor, setEditor] = useState<{ open: boolean; item: TriageLens | null }>({ open: false, item: null });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted">{lenses.length} lenses. Edit a built-in to override it, or add your own interrogation lens.</p>
        <button onClick={() => setEditor({ open: true, item: null })} className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-sm font-semibold text-primary-fg shadow-glow transition-transform hover:scale-[1.03]">
          <Plus className="h-4 w-4" /> Add lens
        </button>
      </div>
      {lenses.map((l) => (
        <ItemRow
          key={l.id}
          title={l.name}
          meta={`${l.appliesTo.join(", ")} · ${l.questions.length} questions`}
          custom={isCustom(l.id)}
          edited={isEdited(l.id)}
          onEdit={() => setEditor({ open: true, item: l })}
          onRemove={() => removeLens(l.id)}
          onRevert={isEdited(l.id) ? () => revertLens(l.id) : undefined}
        />
      ))}
      {editor.open && <LensEditor lens={editor.item} onClose={() => setEditor({ open: false, item: null })} />}
    </div>
  );
}

function ConceptsEditor() {
  const axes = useConceptAxes();
  const isCustomAxis = useCustomContentStore((s) => s.isCustomAxis);
  const addAxis = useCustomContentStore((s) => s.addAxis);
  const removeAxis = useCustomContentStore((s) => s.removeAxis);
  const addValue = useCustomContentStore((s) => s.addValue);
  const removeValue = useCustomContentStore((s) => s.removeValue);
  const [axisName, setAxisName] = useState("");
  const [valueDraft, setValueDraft] = useState<Record<string, string>>({});

  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-border bg-surface p-5">
        <h3 className="mb-2 font-display font-semibold text-text">Add a concept axis</h3>
        <div className="flex gap-2">
          <input value={axisName} onChange={(e) => setAxisName(e.target.value)} placeholder="Axis name (e.g. Setting, Session Length)" className={inputCls} onKeyDown={(e) => { if (e.key === "Enter" && axisName.trim()) { addAxis(axisName); setAxisName(""); } }} />
          <button onClick={() => { if (axisName.trim()) { addAxis(axisName); setAxisName(""); } }} disabled={!axisName.trim()} className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-fg shadow-glow transition-transform enabled:hover:scale-[1.02] disabled:opacity-40">
            <Plus className="h-4 w-4" /> Add axis
          </button>
        </div>
      </div>

      {axes.map((axis) => {
        const custom = isCustomAxis(axis.key);
        const draft = valueDraft[axis.key] ?? "";
        const commit = () => {
          if (draft.trim()) {
            addValue(axis.key, draft);
            setValueDraft((d) => ({ ...d, [axis.key]: "" }));
          }
        };
        return (
          <div key={axis.key} className="rounded-2xl border border-border bg-surface p-5">
            <div className="mb-3 flex items-center gap-2">
              <h3 className="font-display font-semibold text-text">{axis.label}</h3>
              {custom && <span className="rounded-full bg-accent/15 px-1.5 text-[10px] uppercase text-accent">yours</span>}
              <span className="text-xs text-muted">{axis.values.length} values</span>
              <button onClick={() => { if (confirm(custom ? `Delete axis "${axis.label}"?` : `Hide axis "${axis.label}"?`)) removeAxis(axis.key); }} className="ml-auto inline-flex items-center gap-1 text-xs text-muted hover:text-amber-400">
                <Trash2 className="h-3.5 w-3.5" /> {custom ? "Delete axis" : "Hide axis"}
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {axis.values.map((v) => (
                <span key={v} className="inline-flex items-center gap-1 rounded-md border border-border bg-surface-2 px-2 py-0.5 text-xs text-text">
                  {v}
                  <button onClick={() => removeValue(axis.key, v)} aria-label={`Remove ${v}`} className="text-muted hover:text-amber-400"><X className="h-3 w-3" /></button>
                </span>
              ))}
            </div>
            <div className="mt-3 flex gap-2">
              <input value={draft} onChange={(e) => setValueDraft((d) => ({ ...d, [axis.key]: e.target.value }))} onKeyDown={(e) => e.key === "Enter" && commit()} placeholder={`Add a ${axis.label.toLowerCase()} value…`} className={inputCls} />
              <button onClick={commit} disabled={!draft.trim()} className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-border bg-surface-2 px-3 py-2 text-sm font-medium text-text hover:bg-border/40 disabled:opacity-40">
                <Plus className="h-4 w-4" /> Add
              </button>
            </div>
          </div>
        );
      })}
      <p className="text-xs text-muted">
        Concept axes power the Design Challenge roller. New axes and values appear in the Game Concept picker immediately; the Jam reflects value changes within its built-in slots.
      </p>
    </div>
  );
}

function TagsEditor() {
  const tags = useCustomContentStore((s) => s.tags);
  const addTag = useCustomContentStore((s) => s.addTag);
  const removeTag = useCustomContentStore((s) => s.removeTag);
  const [draft, setDraft] = useState("");

  return (
    <div className="rounded-2xl border border-border bg-surface p-5">
      <h3 className="font-display font-semibold text-text">Tags</h3>
      <p className="mb-3 text-sm text-muted">A managed vocabulary of tags you can reuse across content.</p>
      <div className="flex gap-2">
        <input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && draft.trim()) { addTag(draft); setDraft(""); } }} placeholder="Add a tag (e.g. core-loop)" className={inputCls} />
        <button onClick={() => { if (draft.trim()) { addTag(draft); setDraft(""); } }} disabled={!draft.trim()} className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-fg shadow-glow transition-transform enabled:hover:scale-[1.02] disabled:opacity-40">
          <Plus className="h-4 w-4" /> Add
        </button>
      </div>
      {tags.length > 0 ? (
        <div className="mt-4 flex flex-wrap gap-1.5">
          {tags.map((t) => (
            <span key={t} className="inline-flex items-center gap-1 rounded-md border border-border bg-surface-2 px-2 py-0.5 text-xs text-text">
              {t}
              <button onClick={() => removeTag(t)} aria-label={`Remove ${t}`} className="text-muted hover:text-amber-400"><X className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
      ) : (
        <p className="mt-4 text-sm text-muted">No tags yet.</p>
      )}
    </div>
  );
}

function ChallengesEditor() {
  const challenges = useChallenges();
  const isCustom = useCustomContentStore((s) => s.isCustomChallenge);
  const isEdited = useCustomContentStore((s) => s.isEditedChallenge);
  const removeChallenge = useCustomContentStore((s) => s.removeChallenge);
  const revertChallenge = useCustomContentStore((s) => s.revertChallenge);
  const [editor, setEditor] = useState<{ open: boolean; item: ChallengeTemplate | null }>({ open: false, item: null });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted">{challenges.length} challenges. Edit a built-in to override it, or add your own.</p>
        <button onClick={() => setEditor({ open: true, item: null })} className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-sm font-semibold text-primary-fg shadow-glow transition-transform hover:scale-[1.03]">
          <Plus className="h-4 w-4" /> Add challenge
        </button>
      </div>
      {challenges.map((c) => (
        <ItemRow
          key={c.id}
          title={c.title}
          meta={`${c.disciplineIds.join(", ")} · ${c.difficulty} · ${c.estMinutes}m`}
          custom={isCustom(c.id)}
          edited={isEdited(c.id)}
          onEdit={() => setEditor({ open: true, item: c })}
          onRemove={() => removeChallenge(c.id)}
          onRevert={isEdited(c.id) ? () => revertChallenge(c.id) : undefined}
        />
      ))}
      {editor.open && <ChallengeEditor template={editor.item} onClose={() => setEditor({ open: false, item: null })} />}
    </div>
  );
}

function InterviewQuestionsEditor() {
  const questions = useQuestions();
  const isCustom = useCustomContentStore((s) => s.isCustomQuestion);
  const isEdited = useCustomContentStore((s) => s.isEditedQuestion);
  const removeQuestion = useCustomContentStore((s) => s.removeQuestion);
  const revertQuestion = useCustomContentStore((s) => s.revertQuestion);
  const [editor, setEditor] = useState<{ open: boolean; item: InterviewQuestion | null }>({ open: false, item: null });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted">{questions.length} questions. Edit a built-in to override it, or add your own.</p>
        <button onClick={() => setEditor({ open: true, item: null })} className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-sm font-semibold text-primary-fg shadow-glow transition-transform hover:scale-[1.03]">
          <Plus className="h-4 w-4" /> Add question
        </button>
      </div>
      {questions.map((qn) => (
        <ItemRow
          key={qn.id}
          title={qn.prompt}
          meta={`${roleName(qn.roleId)} · ${typeName(qn.typeId)} · ${qn.difficulty}`}
          custom={isCustom(qn.id)}
          edited={isEdited(qn.id)}
          onEdit={() => setEditor({ open: true, item: qn })}
          onRemove={() => removeQuestion(qn.id)}
          onRevert={isEdited(qn.id) ? () => revertQuestion(qn.id) : undefined}
        />
      ))}
      {editor.open && <QuestionEditor question={editor.item} onClose={() => setEditor({ open: false, item: null })} />}
    </div>
  );
}

function ItemRow({
  title,
  meta,
  custom,
  edited,
  onEdit,
  onRemove,
  onRevert,
}: {
  title: string;
  meta: string;
  custom: boolean;
  edited: boolean;
  onEdit: () => void;
  onRemove: () => void;
  onRevert?: () => void;
}) {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-border bg-surface px-3 py-2.5">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="truncate text-sm font-medium text-text">{title}</span>
          {custom && <span className="shrink-0 rounded-full bg-accent/15 px-1.5 text-[10px] uppercase text-accent">yours</span>}
          {edited && <span className="shrink-0 rounded-full bg-amber-400/15 px-1.5 text-[10px] uppercase text-amber-400">edited</span>}
        </div>
        <div className="truncate text-xs text-muted">{meta}</div>
      </div>
      <div className="flex shrink-0 items-center gap-0.5">
        {onRevert && (
          <button onClick={onRevert} aria-label="Revert edits" title="Revert edits" className="grid h-7 w-7 place-items-center rounded-md text-muted hover:bg-surface-2 hover:text-text"><RotateCcw className="h-4 w-4" /></button>
        )}
        <button onClick={onEdit} aria-label="Edit" title="Edit" className="grid h-7 w-7 place-items-center rounded-md text-muted hover:bg-surface-2 hover:text-text"><Pencil className="h-4 w-4" /></button>
        <button onClick={onRemove} aria-label={custom ? "Delete" : "Hide"} title={custom ? "Delete" : "Hide"} className="grid h-7 w-7 place-items-center rounded-md text-muted hover:bg-surface-2 hover:text-amber-400">
          {custom ? <Trash2 className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}

function FiltersEditor() {
  const addedRoles = useCustomContentStore((s) => s.addedRoles);
  const isHiddenRole = useCustomContentStore((s) => s.isHiddenRole);
  const toggleHiddenRole = useCustomContentStore((s) => s.toggleHiddenRole);
  const addRole = useCustomContentStore((s) => s.addRole);
  const removeAddedRole = useCustomContentStore((s) => s.removeAddedRole);

  const addedTypes = useCustomContentStore((s) => s.addedTypes);
  const isHiddenType = useCustomContentStore((s) => s.isHiddenType);
  const toggleHiddenType = useCustomContentStore((s) => s.toggleHiddenType);
  const addType = useCustomContentStore((s) => s.addType);
  const removeAddedType = useCustomContentStore((s) => s.removeAddedType);

  return (
    <div className="space-y-8">
      <NamedListEditor
        title="Interview roles"
        subtitle="Roles you can filter and tag questions by (e.g. Combat Designer)."
        base={BASE_ROLES}
        added={addedRoles}
        isHidden={isHiddenRole}
        onToggleHidden={toggleHiddenRole}
        onAdd={(name, description) => addRole({ name, description })}
        onRemove={removeAddedRole}
      />
      <NamedListEditor
        title="Question types"
        subtitle="Interview formats (e.g. Behavioral, Take-Home, System Design)."
        base={BASE_TYPES}
        added={addedTypes}
        isHidden={isHiddenType}
        onToggleHidden={toggleHiddenType}
        onAdd={(name, description) => addType({ name, description })}
        onRemove={removeAddedType}
      />
      <p className="text-xs text-muted">
        Hidden built-ins drop out of the interview filters; questions tagged with them still load. New roles/types appear in the filters and in the question editor (coming next).
      </p>
    </div>
  );
}

function NamedListEditor({
  title,
  subtitle,
  base,
  added,
  isHidden,
  onToggleHidden,
  onAdd,
  onRemove,
}: {
  title: string;
  subtitle: string;
  base: { id: string; name: string; description?: string }[];
  added: { id: string; name: string; description?: string }[];
  isHidden: (id: string) => boolean;
  onToggleHidden: (id: string) => void;
  onAdd: (name: string, description: string) => void;
  onRemove: (id: string) => void;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  function add() {
    if (!name.trim()) return;
    onAdd(name, description);
    setName("");
    setDescription("");
  }

  return (
    <div className="rounded-2xl border border-border bg-surface p-5">
      <h3 className="font-display font-semibold text-text">{title}</h3>
      <p className="mb-3 text-sm text-muted">{subtitle}</p>

      <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto]">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" className={inputCls} onKeyDown={(e) => e.key === "Enter" && add()} />
        <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description (optional)" className={inputCls} onKeyDown={(e) => e.key === "Enter" && add()} />
        <button onClick={add} disabled={!name.trim()} className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-fg shadow-glow transition-transform enabled:hover:scale-[1.02] disabled:opacity-40">
          <Plus className="h-4 w-4" /> Add
        </button>
      </div>

      <div className="mt-4 grid gap-2 sm:grid-cols-2">
        {base.map((o) => {
          const hidden = isHidden(o.id);
          return (
            <div key={o.id} className={["flex items-center gap-2 rounded-lg border border-border bg-surface-2 px-3 py-2", hidden ? "opacity-50" : ""].join(" ")}>
              <div className="min-w-0 flex-1">
                <div className={["truncate text-sm font-medium", hidden ? "text-muted line-through" : "text-text"].join(" ")}>{o.name}</div>
                {o.description && <div className="truncate text-xs text-muted">{o.description}</div>}
              </div>
              <button onClick={() => onToggleHidden(o.id)} aria-label={hidden ? "Show" : "Hide"} title={hidden ? "Show" : "Hide"} className="grid h-7 w-7 shrink-0 place-items-center rounded-md text-muted hover:bg-surface hover:text-text">
                {hidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          );
        })}
        {added.map((o) => (
          <motion.div key={o.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-2 rounded-lg border border-accent/40 bg-accent/5 px-3 py-2">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <span className="truncate text-sm font-medium text-text">{o.name}</span>
                <span className="rounded-full bg-accent/15 px-1.5 text-[10px] uppercase text-accent">yours</span>
              </div>
              {o.description && <div className="truncate text-xs text-muted">{o.description}</div>}
            </div>
            <button onClick={() => onRemove(o.id)} aria-label="Delete" title="Delete" className="grid h-7 w-7 shrink-0 place-items-center rounded-md text-muted hover:bg-surface hover:text-amber-400">
              <Trash2 className="h-4 w-4" />
            </button>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function DisciplinesEditor() {
  const added = useCustomContentStore((s) => s.addedDisciplines);
  const isHidden = useCustomContentStore((s) => s.isHiddenDiscipline);
  const toggleHidden = useCustomContentStore((s) => s.toggleHiddenDiscipline);
  const addDiscipline = useCustomContentStore((s) => s.addDiscipline);
  const removeAdded = useCustomContentStore((s) => s.removeAddedDiscipline);

  const [name, setName] = useState("");
  const [group, setGroup] = useState(GROUPS[0]);
  const [description, setDescription] = useState("");

  const groups = Array.from(new Set([...GROUPS, ...added.map((d) => d.group)]));

  function add() {
    if (!name.trim()) return;
    addDiscipline({ name, group, description });
    setName("");
    setDescription("");
  }

  return (
    <div className="space-y-6">
      {/* Add form */}
      <div className="rounded-2xl border border-border bg-surface p-5">
        <h3 className="mb-3 font-display font-semibold text-text">Add a discipline</h3>
        <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Discipline name (e.g. World Design)" className={inputCls} onKeyDown={(e) => e.key === "Enter" && add()} />
          <select value={group} onChange={(e) => setGroup(e.target.value)} className={inputCls}>
            {GROUPS.map((g) => (
              <option key={g} value={g}>{g}</option>
            ))}
          </select>
        </div>
        <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Short description (optional)" className={`${inputCls} mt-3`} />
        <button onClick={add} disabled={!name.trim()} className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-fg shadow-glow transition-transform enabled:hover:scale-[1.02] disabled:opacity-40">
          <Plus className="h-4 w-4" /> Add discipline
        </button>
      </div>

      {/* The list, grouped */}
      {groups.map((g) => {
        const builtins = BASE_CATEGORIES.filter((c) => c.group === g);
        const customs = added.filter((c) => c.group === g);
        if (builtins.length === 0 && customs.length === 0) return null;
        return (
          <div key={g}>
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">{g}</div>
            <div className="grid gap-2 sm:grid-cols-2">
              {builtins.map((c) => {
                const hidden = isHidden(c.id);
                return (
                  <div key={c.id} className={["flex items-center gap-2 rounded-lg border border-border bg-surface-2 px-3 py-2", hidden ? "opacity-50" : ""].join(" ")}>
                    <div className="min-w-0 flex-1">
                      <div className={["truncate text-sm font-medium", hidden ? "text-muted line-through" : "text-text"].join(" ")}>{c.name}</div>
                      {c.description && <div className="truncate text-xs text-muted">{c.description}</div>}
                    </div>
                    <button onClick={() => toggleHidden(c.id)} aria-label={hidden ? "Show" : "Hide"} title={hidden ? "Show" : "Hide"} className="grid h-7 w-7 shrink-0 place-items-center rounded-md text-muted hover:bg-surface hover:text-text">
                      {hidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                );
              })}
              {customs.map((c) => (
                <motion.div key={c.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-2 rounded-lg border border-accent/40 bg-accent/5 px-3 py-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="truncate text-sm font-medium text-text">{c.name}</span>
                      <span className="rounded-full bg-accent/15 px-1.5 text-[10px] uppercase text-accent">yours</span>
                    </div>
                    {c.description && <div className="truncate text-xs text-muted">{c.description}</div>}
                  </div>
                  <button onClick={() => removeAdded(c.id)} aria-label="Delete" title="Delete" className="grid h-7 w-7 shrink-0 place-items-center rounded-md text-muted hover:bg-surface hover:text-amber-400">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </motion.div>
              ))}
            </div>
          </div>
        );
      })}

      <p className="text-xs text-muted">
        New disciplines appear everywhere disciplines are used — the Challenge picker, the Library "Practice link", and the Dashboard. Hidden built-ins disappear from those lists but any content already tagged with them still works.
      </p>
    </div>
  );
}

const inputCls =
  "w-full rounded-lg border border-border bg-bg px-3 py-2 text-sm text-text placeholder:text-muted focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary";
